import {sendEvent} from '@github-ui/hydro-analytics'
import {PaperAirplaneIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useRef} from 'react'

import type {GeneratedSuggestion} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {getSelectedThread} from '../utils/get-selected-thread'
import styles from './CopilotSuggestions.module.css'

const CopilotSuggestion = memo(function CopilotSuggestion({
  suggestion,
  onSelect,
}: {
  suggestion: GeneratedSuggestion
  onSelect: (suggestion: GeneratedSuggestion) => void
}) {
  return (
    <ActionList.Item onSelect={() => onSelect(suggestion)} className={styles.suggestionItem}>
      <ActionList.LeadingVisual>
        <Octicon icon={PaperAirplaneIcon} />
      </ActionList.LeadingVisual>
      <span> {suggestion.question}</span>
    </ActionList.Item>
  )
})

const CopilotSuggestions = () => {
  const state = useChatState()
  const manager = useChatManager()
  const shownSuggestionsKeyRef = useRef<string | null>(null)
  const suggestionReferenceGroup = state.suggestions?.referenceType ?? 'default'

  const onClickSuggestion = useCallback(
    (suggestion: GeneratedSuggestion) => {
      manager.markUserInteracted()
      void manager.sendChatMessage({
        thread: getSelectedThread(state),
        content: suggestion.prompt ?? suggestion.question,
        intent: suggestion.intent,
        modeOverride: suggestion.mode,
        references: state.currentReferences,
        topic: state.currentTopic,
        context: state.context,
        model: state.model,
      })

      sendEvent('copilot_generated_suggestion_click', {
        intent: suggestion.intent,
        mode: suggestion.mode,
        contextType: state.context?.[0]?.type,
        content: suggestion.question,
        referenceGroup: suggestionReferenceGroup,
        contextualSuggestionsEnabled: copilotFeatureFlags.contextualChatSuggestionsUpdated,
      })
    },
    [manager, state, suggestionReferenceGroup],
  )

  const isLoading = state.messagesLoading.state === 'loading' || !!state.streamingMessage
  const {suggestions} = state

  const showCopilotSuggestions = Boolean(!isLoading)
  useEffect(() => {
    if (!showCopilotSuggestions || !suggestions?.suggestions?.length) {
      shownSuggestionsKeyRef.current = null
      return
    }

    const shownSuggestionsKey = suggestionReferenceGroup
    if (shownSuggestionsKeyRef.current === shownSuggestionsKey) return
    shownSuggestionsKeyRef.current = shownSuggestionsKey

    sendEvent('copilot_generated_suggestions_shown', {
      referenceGroup: suggestionReferenceGroup,
    })
  }, [showCopilotSuggestions, suggestionReferenceGroup, suggestions])

  return showCopilotSuggestions && !!suggestions && !!suggestions.suggestions ? (
    <ActionList className={clsx('copilot-suggestions', styles.suggestionsList)}>
      <ActionList.Heading as="h2" className={styles.suggestionListHeading}>
        {suggestionHeadingForReferenceType(suggestions.referenceType as string)}
      </ActionList.Heading>
      {suggestions.suggestions.map(s => (
        <CopilotSuggestion key={s.question} suggestion={s} onSelect={onClickSuggestion} />
      ))}
    </ActionList>
  ) : null
}

function suggestionHeadingForReferenceType(referenceType?: string): string {
  if (!referenceType) return 'Ask anything:'
  return `Ask about the ${referenceType.replace(/-/g, ' ')}:`
}

export default memo(CopilotSuggestions)
